import { z } from 'zod'
import { euroToCents, slaDueAt } from '../sla'

export const bookerTypes = ['planner', 'venue', 'agency', 'private', 'corporate'] as const
export type BookerType = (typeof bookerTypes)[number]

export const bookerTypeLabels: Record<BookerType, string> = {
  private: 'Privat (eigene Feier)',
  corporate: 'Firma',
  planner: 'Hochzeits- oder Eventplanung',
  venue: 'Location',
  agency: 'Eventagentur',
}

// One schema for the form and the API route. German messages, because they are shown to the user.
export const bookingRequestInput = z.object({
  bookerName: z.string().trim().min(2, 'Bitte sag uns deinen Namen.').max(120),
  bookerEmail: z
    .string()
    .trim()
    .toLowerCase()
    .email('Diese E-Mail-Adresse sieht nicht gültig aus.'),
  bookerPhone: z.string().trim().max(40).optional().or(z.literal('')),
  bookerType: z.enum(bookerTypes, { message: 'Bitte wähle aus, für wen du anfragst.' }),
  occasion: z.string().trim().min(2, 'Was ist der Anlass?').max(120),
  eventDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Bitte gib das Datum als JJJJ-MM-TT an.')
    .refine((value) => !Number.isNaN(Date.parse(value)), 'Dieses Datum gibt es nicht.')
    .refine(
      (value) => new Date(`${value}T12:00:00Z`).getTime() > Date.now(),
      'Das Datum liegt in der Vergangenheit.'
    ),
  city: z.string().trim().min(2, 'In welcher Stadt ist die Feier?').max(120),
  postcode: z
    .string()
    .trim()
    .regex(/^\d{5}$/, 'Eine deutsche Postleitzahl hat fünf Ziffern.'),
  lineupWish: z.string().trim().max(200).optional().or(z.literal('')),
  budgetMinEuro: z.coerce.number().int().min(0).max(1_000_000).optional(),
  budgetMaxEuro: z.coerce.number().int().min(0).max(1_000_000).optional(),
  message: z.string().trim().max(4000).optional().or(z.literal('')),
  consent: z.literal(true, {
    message: 'Ohne deine Einwilligung dürfen wir die Anfrage nicht speichern.',
  }),
  // honeypot: a real person never fills this, bots fill every field they find. Accepted by the
  // schema on purpose - the API route answers 200 and stores nothing, so a bot learns nothing.
  website: z.string().max(400).optional(),
})

export type BookingRequestInput = z.infer<typeof bookingRequestInput>

export const bookingRequestSchema = bookingRequestInput.refine(
  (v) =>
    v.budgetMinEuro === undefined ||
    v.budgetMaxEuro === undefined ||
    v.budgetMaxEuro >= v.budgetMinEuro,
  { message: 'Das Maximum darf nicht unter dem Minimum liegen.', path: ['budgetMaxEuro'] }
)

/** Everything the database row needs, with money in cents and the SLA deadline computed. */
export function toBookingRequestRow(input: BookingRequestInput, createdAt = new Date()) {
  const blank = (value?: string) => (value && value.length > 0 ? value : null)
  return {
    bookerName: input.bookerName,
    bookerEmail: input.bookerEmail,
    bookerPhone: blank(input.bookerPhone),
    bookerType: input.bookerType,
    occasion: input.occasion,
    eventDate: new Date(`${input.eventDate}T12:00:00Z`),
    city: input.city,
    postcode: input.postcode,
    lineupWish: blank(input.lineupWish),
    budgetMinCents: input.budgetMinEuro === undefined ? null : euroToCents(input.budgetMinEuro),
    budgetMaxCents: input.budgetMaxEuro === undefined ? null : euroToCents(input.budgetMaxEuro),
    message: blank(input.message),
    consentAt: createdAt,
    slaDueAt: slaDueAt(createdAt),
  }
}

/** Zod issues flattened to { field: message } for the form. */
export function fieldErrors(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {}
  for (const issue of error.issues) {
    const key = issue.path.join('.') || '_'
    if (!out[key]) out[key] = issue.message
  }
  return out
}
